// Přihlášení a firmy: stav z GET /v1/me, akce nad Better Auth a nad API firem a admina.
// Chyby se vracejí jako klíč i18n, překládá je až view.
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { toE164 } from '@wac-buddy/shared'
import type { AuthConfigResponse, CreateOrgInput, MeOrg, MeResponse } from '@wac-buddy/shared'
import { api, ApiClientError } from '@/api/client'
import { authClient } from '@/auth/client'
import { useBrandStore } from './brand'
import { useBuddyStore } from './buddy'
import { useCalendarStore } from './calendar'
import { useCalendarsStore } from './calendars'
import { useChannelsStore } from './channels'
import { useClientsStore } from './clients'
import { useJobsStore } from './jobs'
import { useOrgStore } from './org'
import { usePromisesStore } from './promises'
import { useTasksStore } from './tasks'
import { useTodayStore } from './today'
import { useVoiceStore } from './voice'

export type AuthStatus = 'idle' | 'loading' | 'ready'
export type ActionResult = { ok: true } | { ok: false; errorKey: string }
export type SocialProvider = 'google' | 'apple'

const ok: ActionResult = { ok: true }
const fail = (errorKey: string): ActionResult => ({ ok: false, errorKey })

interface AuthErrorLike {
  code?: string
  message?: string
  status?: number
}

/** Chyba z Better Auth (`{ code, message, status }`) na klíč i18n. */
export function authErrorKey(
  scope: 'login' | 'register',
  error: AuthErrorLike | null | undefined,
): string {
  const code = error?.code ?? ''
  if (!error?.status && !code) return 'login.errors.network'
  if (scope === 'login' && code === 'INVALID_EMAIL_OR_PASSWORD') return 'login.errors.invalid'
  if (scope === 'register') {
    if (code.startsWith('USER_ALREADY_EXISTS')) return 'register.errors.exists'
    if (code === 'PASSWORD_TOO_SHORT') return 'register.errors.weak'
  }
  return `${scope}.errors.generic`
}

/** Better Auth vrátí chybu bez statusu i kódu, když se k serveru nedostal. */
function isAuthNetworkError(error: AuthErrorLike | null | undefined): boolean {
  return !error?.status && !error?.code
}

function isNetworkError(e: unknown): boolean {
  return !(e instanceof ApiClientError)
}

export const useAuthStore = defineStore('auth', () => {
  const me = ref<MeResponse | null>(null)
  const status = ref<AuthStatus>('idle')
  const authConfig = ref<AuthConfigResponse | null>(null)

  const isAuthenticated = computed(() => me.value !== null)
  const isAdmin = computed(() => me.value?.isAdmin === true)
  const impersonatedBy = computed(() => me.value?.impersonatedBy ?? null)

  // Jiný uživatel nebo jiná firma = jiná data: seznamy a cache klientů a zakázek se zahodí.
  watch(
    () => `${me.value?.user.id ?? ''}:${me.value?.org?.id ?? ''}`,
    (next, prev) => {
      if (next === prev) return
      useClientsStore().invalidate()
      useJobsStore().invalidate()
      useCalendarStore().invalidate()
      usePromisesStore().invalidate()
      useTasksStore().invalidate()
      useTodayStore().invalidate()
      useOrgStore().invalidate()
      useBrandStore().invalidate()
      useBuddyStore().invalidate()
      useVoiceStore().invalidate()
      useChannelsStore().invalidate()
      useCalendarsStore().invalidate()
    },
  )

  /**
   * Načte session. 401 znamená „nepřihlášen“. Síťová chyba nebo výpadek api se jednou zopakuje
   * (krátký výpadek nesmí uživatele vyhodit na přihlášení), pak se také bere jako nepřihlášen.
   * Status skončí vždy `ready`.
   */
  async function load(): Promise<void> {
    status.value = 'loading'
    try {
      me.value = await api<MeResponse>('/me')
    } catch (e) {
      if (!isNetworkError(e) && (e as { status?: number }).status === 401) {
        me.value = null
      } else {
        await new Promise((r) => setTimeout(r, 400))
        try {
          me.value = await api<MeResponse>('/me')
        } catch {
          me.value = null
        }
      }
    } finally {
      status.value = 'ready'
    }
  }

  async function loadAuthConfig(): Promise<void> {
    try {
      authConfig.value = await api<AuthConfigResponse>('/auth-config')
    } catch {
      authConfig.value = null
    }
  }

  /** Po úspěšném přihlášení načte `me`. Když session chybí (cookie se nepřenesla), je to chyba. */
  async function finishSignIn(genericKey: string): Promise<ActionResult> {
    await load()
    return me.value ? ok : fail(genericKey)
  }

  async function signIn(email: string, password: string): Promise<ActionResult> {
    try {
      const { error } = await authClient.signIn.email({ email, password })
      if (error) return fail(authErrorKey('login', error))
    } catch {
      return fail('login.errors.network')
    }
    return finishSignIn('login.errors.generic')
  }

  async function signUp(name: string, email: string, password: string): Promise<ActionResult> {
    try {
      const { error } = await authClient.signUp.email({ name, email, password })
      if (error) return fail(authErrorKey('register', error))
    } catch {
      return fail('login.errors.network')
    }
    return finishSignIn('register.errors.generic')
  }

  /** Odkaz vede po přihlášení na `next`, nového uživatele na založení firmy. */
  async function sendMagicLink(email: string, next = '/'): Promise<ActionResult> {
    const origin = window.location.origin
    try {
      const { error } = await authClient.signIn.magicLink({
        email,
        callbackURL: `${origin}${next}`,
        newUserCallbackURL: `${origin}/zalozeni-firmy`,
        errorCallbackURL: `${origin}/login`,
      })
      if (error) return fail(authErrorKey('login', error))
    } catch {
      return fail('login.errors.network')
    }
    return ok
  }

  /** Přesměruje celé okno na poskytovatele, vrací se jen při chybě. */
  async function signInSocial(provider: SocialProvider, next = '/'): Promise<ActionResult> {
    const origin = window.location.origin
    try {
      const { error } = await authClient.signIn.social({
        provider,
        callbackURL: `${origin}${next}`,
        newUserCallbackURL: `${origin}/zalozeni-firmy`,
        errorCallbackURL: `${origin}/login`,
      })
      if (error) return fail(authErrorKey('login', error))
    } catch {
      return fail('login.errors.network')
    }
    return ok
  }

  async function signOut(): Promise<void> {
    try {
      await authClient.signOut()
    } finally {
      me.value = null
    }
    const { router } = await import('@/router')
    await router.push('/login')
  }

  async function createOrg(input: CreateOrgInput): Promise<ActionResult> {
    try {
      await api<MeOrg>('/org', { method: 'POST', body: JSON.stringify(input) })
    } catch (e) {
      return fail(isNetworkError(e) ? 'login.errors.network' : 'onboarding.errors.generic')
    }
    await load()
    return me.value?.org ? ok : fail('onboarding.errors.generic')
  }

  async function switchOrg(organizationId: string): Promise<ActionResult> {
    try {
      me.value = await api<MeResponse>('/org/switch', {
        method: 'POST',
        body: JSON.stringify({ organizationId }),
      })
    } catch (e) {
      return fail(isNetworkError(e) ? 'login.errors.network' : 'profile.errors.switch')
    }
    return ok
  }

  async function impersonate(orgId: string, reason: string): Promise<ActionResult> {
    try {
      me.value = await api<MeResponse>(`/admin/impersonate/${encodeURIComponent(orgId)}`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      })
    } catch (e) {
      return fail(isNetworkError(e) ? 'login.errors.network' : 'admin.errors.generic')
    }
    return ok
  }

  async function stopImpersonating(): Promise<ActionResult> {
    try {
      me.value = await api<MeResponse>('/admin/impersonate/stop', { method: 'POST' })
    } catch (e) {
      return fail(isNetworkError(e) ? 'login.errors.network' : 'admin.errors.generic')
    }
    return ok
  }

  /** Jméno a telefon (prázdný telefon = smazat). Po uložení se znovu načte `me`. */
  async function updateProfile(input: { name: string; phone: string }): Promise<ActionResult> {
    // Telefon se ukládá jako E.164 (CLAUDE.md pravidlo 11).
    const rawPhone = input.phone.trim()
    const phone = rawPhone ? toE164(rawPhone) : null
    if (rawPhone && phone === null) return fail('settings.profile.errors.phone')
    try {
      // `phone` je additionalField na serveru, typy klienta o něm nevědí.
      const { error } = await authClient.updateUser({
        name: input.name,
        phone,
      } as Parameters<typeof authClient.updateUser>[0])
      if (error) {
        return fail(isAuthNetworkError(error) ? 'common.network' : 'settings.profile.errors.save')
      }
    } catch {
      return fail('common.network')
    }
    await load()
    return ok
  }

  /** Změna hesla, ostatní zařízení zůstanou přihlášená. */
  async function changePassword(input: {
    currentPassword: string
    newPassword: string
  }): Promise<ActionResult> {
    try {
      const { error } = await authClient.changePassword({
        currentPassword: input.currentPassword,
        newPassword: input.newPassword,
        revokeOtherSessions: false,
      })
      if (error) {
        if (isAuthNetworkError(error)) return fail('common.network')
        if (error.code === 'INVALID_PASSWORD')
          return fail('settings.profile.password.errors.current')
        if (error.code === 'PASSWORD_TOO_SHORT')
          return fail('settings.profile.password.errors.short')
        return fail('settings.profile.password.errors.generic')
      }
    } catch {
      return fail('common.network')
    }
    return ok
  }

  /** Žádost o odkaz na nové heslo. Odpověď serveru se neprozrazuje, selhat může jen spojení. */
  async function requestPasswordReset(email: string): Promise<ActionResult> {
    try {
      const { error } = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/nove-heslo`,
      })
      if (error && isAuthNetworkError(error)) return fail('common.network')
    } catch {
      return fail('common.network')
    }
    return ok
  }

  /** Nastavení nového hesla podle tokenu z odkazu. */
  async function resetPassword(newPassword: string, token: string): Promise<ActionResult> {
    try {
      const { error } = await authClient.resetPassword({ newPassword, token })
      if (error) {
        if (isAuthNetworkError(error)) return fail('common.network')
        if (error.code === 'INVALID_TOKEN') return fail('password.reset.errors.invalid')
        if (error.code === 'PASSWORD_TOO_SHORT') return fail('password.reset.errors.short')
        return fail('password.reset.errors.generic')
      }
    } catch {
      return fail('common.network')
    }
    return ok
  }

  /** Přijetí pozvánky do firmy: backend přidá členství a vrátí nový stav `me` (aktivní je nová firma). */
  async function acceptInvitation(invitationId: string): Promise<ActionResult> {
    try {
      me.value = await api<MeResponse>(`/invitations/${encodeURIComponent(invitationId)}/accept`, {
        method: 'POST',
      })
    } catch (e) {
      if (isNetworkError(e)) return fail('common.network')
      return fail(
        (e as { status?: number }).status === 403
          ? 'invitation.errors.forAnotherEmail'
          : 'invitation.errors.accept',
      )
    }
    return ok
  }

  return {
    me,
    status,
    authConfig,
    isAuthenticated,
    isAdmin,
    impersonatedBy,
    load,
    loadAuthConfig,
    signIn,
    signUp,
    sendMagicLink,
    signInSocial,
    signOut,
    createOrg,
    switchOrg,
    impersonate,
    stopImpersonating,
    acceptInvitation,
    updateProfile,
    changePassword,
    requestPasswordReset,
    resetPassword,
  }
})
